import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { GBP_ENABLED } from '../lib/featureFlags';
import { reportMonthToDate, runReport, type ReportResult } from '../lib/reports';
import type { GbpPerfTotals } from './useGbpPerformance';

export interface GbpDirectLocation {
  id: string;
  name: string | null;
}

export interface AggregatedGbpPerformance {
  /** True while the connection list or report calls are in flight. */
  loading: boolean;
  /** At least one active Google Business connection (direct or Ayrshare). */
  connected: boolean;
  /** Active locations included in the connection check. */
  locationCount: number;
  /** Direct Google connections the full-insights modal can open. */
  directLocations: GbpDirectLocation[];
  /**
   * Connected, but every location is an aggregator, so there are no
   * performance numbers to sum.
   */
  unsupported: boolean;
  /** This month so far across direct Google locations. Null when none loaded. */
  totals: GbpPerfTotals | null;
  /** The same days of last month, for the trend chips. */
  previousTotals: GbpPerfTotals | null;
  /** Last day Google has published for this month, or null when none yet. */
  dataThrough: string | null;
}

const EMPTY: AggregatedGbpPerformance = {
  loading: false,
  connected: false,
  locationCount: 0,
  directLocations: [],
  unsupported: false,
  totals: null,
  previousTotals: null,
  dataThrough: null,
};

const METRICS =
  'views, search_views, maps_views, calls, website_clicks, directions, bookings, messages, interactions';

function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function num(row: Record<string, string | number> | undefined, key: string): number {
  const raw = row?.[key];
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function totalsFrom(result: ReportResult): GbpPerfTotals {
  const row = result.rows[0];
  return {
    // The dataset does not split views by device, so the per-device
    // fields are not available here; search and maps are.
    impressionsDesktopMaps: 0,
    impressionsDesktopSearch: 0,
    impressionsMobileMaps: 0,
    impressionsMobileSearch: 0,
    impressions: num(row, 'views'),
    impressionsSearch: num(row, 'search_views'),
    impressionsMaps: num(row, 'maps_views'),
    conversations: num(row, 'messages'),
    directionRequests: num(row, 'directions'),
    calls: num(row, 'calls'),
    websiteClicks: num(row, 'website_clicks'),
    bookings: num(row, 'bookings'),
    interactions: num(row, 'interactions'),
  };
}

/**
 * One compiled Google Business performance total for the Control Centre,
 * read from the `google_business` report dataset (the stored daily
 * metrics for every active direct `provider = 'google'` location).
 *
 * Google publishes each day a few days late, so the window runs from the
 * 1st to yesterday and is compared with the same days of last month.
 * Ayrshare-only businesses stay "connected" but report unsupported, because
 * that connection has no Performance API.
 */
export function useAggregatedGbpPerformance(
  businessId: string | null | undefined,
  timeZone: string = 'UTC',
): AggregatedGbpPerformance {
  const [state, setState] = useState<AggregatedGbpPerformance>({ ...EMPTY, loading: true });

  useEffect(() => {
    let cancelled = false;

    if (!GBP_ENABLED || !businessId) {
      setState(EMPTY);
      return;
    }

    setState((current) => ({ ...current, loading: true }));

    void (async () => {
      const { data: connections, error } = await supabase
        .from('google_business_connections')
        .select('id, provider, location_name')
        .eq('business_id', businessId)
        .in('provider', ['ayrshare', 'google'])
        .eq('is_active', true);

      if (cancelled) return;

      const rows = connections || [];
      if (error || rows.length === 0) {
        setState(EMPTY);
        return;
      }

      const direct = rows.filter((row) => row.provider === 'google');
      const directLocations: GbpDirectLocation[] = direct.map((row) => ({
        id: row.id,
        name: (row.location_name as string | null) || null,
      }));
      if (direct.length === 0) {
        setState({
          loading: false,
          connected: true,
          locationCount: rows.length,
          directLocations: [],
          unsupported: true,
          totals: null,
          previousTotals: null,
          dataThrough: null,
        });
        return;
      }

      // 1st of the month through yesterday; Google has nothing for today yet.
      const mtd = reportMonthToDate(timeZone, 0);
      const until = mtd.until > mtd.since ? shiftDate(mtd.until, -1) : mtd.since;
      const span = Number(until.slice(8, 10)) - 1;
      const lastMonth = reportMonthToDate(timeZone, -1);
      const prevUntilDay = Math.min(1 + span, Number(lastMonth.until.slice(8, 10)));
      const prevUntil = `${lastMonth.since.slice(0, 8)}${String(prevUntilDay).padStart(2, '0')}`;

      try {
        const [current, previous, byDay] = await Promise.all([
          runReport(businessId, `FROM google_business SHOW ${METRICS} SINCE ${mtd.since} UNTIL ${until}`),
          runReport(businessId, `FROM google_business SHOW ${METRICS} SINCE ${lastMonth.since} UNTIL ${prevUntil}`),
          runReport(businessId, `FROM google_business SHOW views, interactions GROUP BY day SINCE ${mtd.since} UNTIL ${until}`),
        ]);
        if (cancelled) return;

        const published = byDay.rows.filter((row) => num(row, 'views') > 0 || num(row, 'interactions') > 0);
        const dataThrough = published.length > 0 ? String(published[published.length - 1].day) : null;

        setState({
          loading: false,
          connected: true,
          locationCount: rows.length,
          directLocations,
          unsupported: false,
          totals: totalsFrom(current),
          previousTotals: totalsFrom(previous),
          dataThrough,
        });
      } catch (err) {
        console.error('[useAggregatedGbpPerformance]', err);
        if (cancelled) return;
        setState({
          loading: false,
          connected: true,
          locationCount: rows.length,
          directLocations,
          unsupported: false,
          totals: null,
          previousTotals: null,
          dataThrough: null,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [businessId, timeZone]);

  return state;
}
